import {useState} from 'react';

import ArrowDown from '../../../../assets/icons/arrow-down.svg';
import Modal from '../../../../components/tailwind/Modal';
import {CheckoutResponse} from '../api';

// OrderSummary Component
const OrderSummary: React.FC<{
  checkoutData: CheckoutResponse;
  businessName?: string;
}> = ({checkoutData, businessName}) => {
  const [detailsOpen, setDetailsOpen] = useState(false);

  const deliveryFee = checkoutData?.deliveryDetails?.fee ?? 0;
  const subtotal =
    checkoutData?.orderDetails?.reduce((sum, item) => sum + item?.price, 0) ??
    0;

  return (
    <div className="flex flex-col justify-center items-center lg:items-start">
      <div className="flex flex-col w-full max-w-md">
        <div className="mb-3 md:mb-8">
          <p className="text-xs text-captionGrey font-semibold mb-[10px]">
            Pay to: {businessName || 'Cashia'}
          </p>
          <h2 className="text-4xl font-semibold text-displayBlack">{`${
            checkoutData.currency || 'KES'
          } ${checkoutData.amount.toLocaleString()}`}</h2>
        </div>

        <div className="lg:hidden self-center">
          <button
            className="bg-gray-200 text-gray-700 px-4 py-2 rounded-md flex items-center justify-center"
            onClick={() => setDetailsOpen(!detailsOpen)}>
            See Details <img src={ArrowDown} alt="Toggle" className="ml-2" />
          </button>
          <Modal
            isVisible={detailsOpen}
            onClose={() => setDetailsOpen(false)}
            containerStyle="items-start"
            className="px-4 pt-12 pb-5 w-full rounded-none rounded-bl-2xl rounded-br-2xl md:w-full lg:w-[600px]"
            showCloseIcon={true}
            dismissOnClickOutside={true}>
            <div className="mt-8">
              <OrderSummaryDetails
                checkoutData={checkoutData}
                detailsOpen={true}
                subtotal={subtotal}
                deliveryFee={deliveryFee}
              />
            </div>
          </Modal>
        </div>

        {/* Desktop: Always show details */}
        <div className="hidden lg:block">
          <OrderSummaryDetails
            checkoutData={checkoutData}
            detailsOpen={true}
            subtotal={subtotal}
            deliveryFee={deliveryFee}
          />
        </div>
      </div>
    </div>
  );
};

export default OrderSummary;

const OrderSummaryDetails: React.FC<{
  checkoutData: CheckoutResponse;
  detailsOpen: boolean;
  subtotal: number;
  deliveryFee: number;
}> = ({checkoutData, detailsOpen, subtotal, deliveryFee}) => {
  return (
    <div className={`lg:block ${detailsOpen ? 'block' : 'hidden'}`}>
      {checkoutData?.orderDetails?.map((item, index) => (
        <div key={index} className="">
          <div className="flex text-headlineGrayscale text-lg justify-between font-semibold mb-2">
            <h3 className="text-base text-headlineGrayscale ">{item.name}</h3>
            <span className="text-base text-headlineGrayscale ">
              {`${item.currency} ${(
                item.quantity * item.price
              ).toLocaleString()}`}
            </span>
          </div>
          <p className="text-sm text-secondaryGrayscale mb-3">
            {item.description}
          </p>
          <div className="flex justify-between text-sm text-secondaryGrayscale mb-2 md:mb-4">
            <span className="text-xs font-semibold">{`Qty: ${item.quantity}`}</span>
            <span className="text-xs font-semibold">{`${
              item.currency
            } ${item.price.toLocaleString()} Each`}</span>
          </div>
        </div>
      ))}

      <div className="mt-3 mb-3 pt-3 md:pt-4 border-t border-subtleGrey">
        {checkoutData?.orderDetails?.length > 0 && (
          <div className="flex text-headlineGrayscale text-lg font-semibold justify-between mb-2 md:mb-3">
            <span>Subtotal</span>
            <span>{`${
              checkoutData.currency || 'KES'
            } ${subtotal.toLocaleString()}`}</span>
          </div>
        )}

        {checkoutData?.deliveryDetails && (
          <div className="flex justify-between text-headlineGrayscale text-lg font-semibold md:mb-3">
            <span>Delivery Fee</span>
            <span>{`${
              checkoutData.currency || 'KES'
            } ${deliveryFee.toLocaleString()}`}</span>
          </div>
        )}

        {(checkoutData?.orderDetails?.length > 0 ||
          checkoutData?.deliveryDetails) && (
          <div className="mt-3 mb-0 md:mb-3 pt-0 md:pt-4 border-t border-subtleGrey" />
        )}

        <div className="flex text-headlineGrayscale text-lg font-semibold justify-between mt-4">
          <span>Total due</span>
          <span>{`${
            checkoutData.currency || 'KES'
          } ${checkoutData.amount.toLocaleString()}`}</span>
        </div>
      </div>
    </div>
  );
};
